import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Play,
  Plus,
  RefreshCcw,
  Trash2,
  KeyRound,
  Clock3,
  CircleX,
} from "lucide-react";
import {
  createRotationJob,
  deleteRotationJob,
  friendlyAuthError,
  listRotationJobs,
  loadSecrets,
  loadVaults,
  runRotationJobNow,
  type RotationJobRow,
} from "@/lib/api";
import type { SecretRow } from "@/lib/secretTypes";

type VaultLite = { id: string; name: string };

const PRESETS: Array<{ label: string; seconds: number }> = [
  { label: "1 hour", seconds: 60 * 60 },
  { label: "12 hours", seconds: 12 * 60 * 60 },
  { label: "24 hours", seconds: 24 * 60 * 60 },
  { label: "7 days", seconds: 7 * 24 * 60 * 60 },
  { label: "30 days", seconds: 30 * 24 * 60 * 60 },
  { label: "90 days", seconds: 90 * 24 * 60 * 60 },
];

const VALUE_KINDS = [
  { id: "hex", label: "Hex (lowercase)" },
  { id: "base64url", label: "Base64URL" },
  { id: "alphanum", label: "Alphanumeric" },
];

function fmtRelative(date: string | null): string {
  if (!date) return "—";
  try {
    const d = new Date(date).getTime();
    const delta = Math.round((d - Date.now()) / 1000);
    const abs = Math.abs(delta);
    const sign = delta >= 0 ? "in " : "";
    const suffix = delta >= 0 ? "" : " ago";
    if (abs < 60) return `${sign}${abs}s${suffix}`;
    if (abs < 3600) return `${sign}${Math.round(abs / 60)}m${suffix}`;
    if (abs < 86400) return `${sign}${Math.round(abs / 3600)}h${suffix}`;
    return `${sign}${Math.round(abs / 86400)}d${suffix}`;
  } catch {
    return "—";
  }
}

function fmtInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

export function RotationSection({ orgId }: { orgId: string }) {
  const [jobs, setJobs] = useState<RotationJobRow[]>([]);
  const [secrets, setSecrets] = useState<SecretRow[]>([]);
  const [vaults, setVaults] = useState<VaultLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [vaultId, setVaultId] = useState<string>("");
  const [secretId, setSecretId] = useState<string>("");
  const [intervalSeconds, setIntervalSeconds] = useState<number>(24 * 60 * 60);
  const [valueKind, setValueKind] = useState<"hex" | "base64url" | "alphanum">("hex");
  const [valueLength, setValueLength] = useState<number>(32);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [j, s, v] = await Promise.all([
        listRotationJobs(orgId),
        loadSecrets(orgId, {}),
        loadVaults(orgId),
      ]);
      setJobs(j);
      setSecrets(s as SecretRow[]);
      setVaults(
        (v as Array<{ id: string; name: string }>).map((row) => ({
          id: row.id,
          name: row.name,
        })),
      );
      setErr(null);
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  const filteredSecrets = useMemo(
    () => (vaultId ? secrets.filter((s) => s.vault_id === vaultId) : secrets),
    [secrets, vaultId],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!secretId) {
      setErr("Pick a secret to rotate.");
      return;
    }
    setCreating(true);
    try {
      await createRotationJob(orgId, {
        secret_id: secretId,
        interval_seconds: intervalSeconds,
        metadata: { kind: valueKind, length: valueLength },
      });
      setSecretId("");
      setVaultId("");
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setCreating(false);
    }
  }

  async function runNow(id: string) {
    setBusy(id);
    try {
      await runRotationJobNow(orgId, id);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this rotation schedule? Past versions are kept.")) return;
    setBusy(id);
    try {
      await deleteRotationJob(orgId, id);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text)]">Rotation jobs</h2>
          <p className="text-xs text-[var(--color-text-subtle)]">
            Auto-rotate secret values on a schedule. Requires{" "}
            <code className="rounded bg-[var(--color-surface-2)] px-1 py-0.5 text-[10px]">
              ROTATION_ENABLED=1
            </code>{" "}
            on the API process.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="btn btn-secondary py-1.5 text-xs"
          disabled={loading}
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </header>

      {err && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          <CircleX className="mr-1 inline h-3.5 w-3.5" /> {err}
        </div>
      )}

      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h3 className="mb-3 text-sm font-medium text-[var(--color-text)]">
          Schedule a new rotation
        </h3>
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
            Vault
            <select
              className="field-input"
              value={vaultId}
              onChange={(e) => {
                setVaultId(e.target.value);
                setSecretId("");
              }}
            >
              <option value="">All vaults</option>
              {vaults.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
            Secret
            <select
              className="field-input"
              value={secretId}
              onChange={(e) => setSecretId(e.target.value)}
              required
            >
              <option value="">Pick a secret…</option>
              {filteredSecrets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
            Rotation interval
            <select
              className="field-input"
              value={intervalSeconds}
              onChange={(e) => setIntervalSeconds(Number(e.target.value))}
            >
              {PRESETS.map((p) => (
                <option key={p.seconds} value={p.seconds}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs text-[var(--color-text-muted)]">
            Generated value
            <div className="flex gap-2">
              <select
                className="field-input flex-1"
                value={valueKind}
                onChange={(e) =>
                  setValueKind(e.target.value as "hex" | "base64url" | "alphanum")
                }
              >
                {VALUE_KINDS.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={8}
                max={256}
                className="field-input w-24"
                value={valueLength}
                onChange={(e) => setValueLength(Math.max(8, Math.min(256, Number(e.target.value))))}
              />
            </div>
          </label>

          <div className="sm:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={creating}
              className="btn btn-primary py-2 text-sm"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Schedule rotation
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="border-b border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-text)]">
          Active schedules
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[var(--color-surface-2)] text-xs uppercase tracking-wide text-[var(--color-text-subtle)]">
              <tr>
                <th className="px-4 py-2">Secret</th>
                <th className="px-4 py-2">Vault</th>
                <th className="px-4 py-2">Interval</th>
                <th className="px-4 py-2">Next run</th>
                <th className="px-4 py-2">Last run</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-xs text-[var(--color-text-subtle)]">
                    <Loader2 className="mr-1 inline h-3.5 w-3.5 spin" /> Loading rotation jobs…
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-xs text-[var(--color-text-subtle)]">
                    No rotation schedules yet.
                  </td>
                </tr>
              ) : (
                jobs.map((j) => (
                  <tr key={j.id} className="border-t border-[var(--color-border)]">
                    <td className="px-4 py-2.5 font-medium text-[var(--color-text)]">
                      <span className="inline-flex items-center gap-1.5">
                        <KeyRound className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                        {j.secret_name ?? "(deleted secret)"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-text-muted)]">
                      {j.vault_name ?? "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1 rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 text-[11px]">
                        <Clock3 className="h-3 w-3" /> {fmtInterval(j.interval_seconds)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-text-muted)]">
                      {fmtRelative(j.next_run_at)}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--color-text-muted)]">
                      {fmtRelative(j.last_run_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={
                          j.last_error
                            ? "rounded bg-red-100 px-1.5 py-0.5 text-[11px] text-red-700"
                            : j.failure_count > 0
                              ? "rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-700"
                              : "rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] text-emerald-700"
                        }
                        title={j.last_error ?? undefined}
                      >
                        {j.last_error ? `failed (${j.failure_count}×)` : j.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          className="btn btn-secondary py-1 text-[11px]"
                          disabled={busy === j.id}
                          onClick={() => void runNow(j.id)}
                        >
                          <Play className="h-3 w-3" /> Run now
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary py-1 text-[11px] text-red-700"
                          disabled={busy === j.id}
                          onClick={() => void remove(j.id)}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
